/**
 * Phase 6 — Remote Monitoring View (CRA-facing SDV surface).
 *
 * Covers AC1-6 (layout + subject list + subject detail + form
 * completion + query counts), AC7-8 (PHI redaction badge already
 * applied server-side per the phi/redactor service — this surface
 * shows the badge), AC9 (read-only), AC10 (assigned subjects only —
 * server-enforced), AC11 (every read writes access log — server-
 * emitted), AC12 (site-scoped), AC14 (SDV completion pill per form
 * instance), AC15 (SDV queue), AC16 (access-window banner + gate
 * message on 403), AC18 (aging widget), AC19 (RBM export button).
 *
 * The AC17 heatmap widget is deferred pending client confirmation on
 * axes (sites × subjects vs subjects × visits — see plan §phase-6-open).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ApiError,
  remoteMonitoring,
  studies,
  type SdvStatus,
} from '@/api/client';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  Loader2,
  Radio,
  ShieldCheck,
} from 'lucide-react';
import { fmtDateTime, fmtVisitDay, visitActualDateLabel } from '@/lib/dates';
import { useHasRole } from '@/components/RoleGate';
import { errorText } from '@/lib/permissionError';
import { studyLabel } from '@/lib/studyLabel';
import { subjectDisplayId } from '@/lib/subjectLabel';

const SDV_BADGE: Record<SdvStatus, string> = {
  not_started: 'bg-slate-100 text-slate-600 border-slate-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  complete: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const SDV_LABEL: Record<SdvStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

const MONITOR_ROLES = ['monitor', 'platform_admin', 'application_admin'] as const;

export function RemoteMonitoringTab() {
  const isMonitor = useHasRole(MONITOR_ROLES);
  const [studyId, setStudyId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);

  const studiesQ = useQuery({
    queryKey: ['studies', 'for-remote-monitoring'],
    queryFn: () => studies.list(),
    staleTime: 60_000,
  });

  const subjectsQ = useQuery({
    queryKey: ['remote-monitoring', 'subjects', studyId],
    queryFn: () => remoteMonitoring.listSubjects(studyId),
    enabled: !!studyId,
    retry: false, // AC16 — surface the outside-window rejection promptly
  });

  const outsideWindow =
    subjectsQ.error instanceof ApiError && subjectsQ.error.status === 403;

  const agingQ = useQuery({
    queryKey: ['remote-monitoring', 'sdv-aging', studyId],
    queryFn: () => remoteMonitoring.sdvAging(studyId),
    enabled: !!studyId && !outsideWindow,
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 rounded border border-surface-border bg-surface-card p-3">
        <label className="text-xs">
          <span className="mb-1 block font-medium text-ink-secondary">Study *</span>
          <select
            value={studyId}
            onChange={(e) => {
              setStudyId(e.target.value);
              setSelectedSubjectId(null);
            }}
            className="rounded border border-surface-border px-2 py-1 text-sm"
          >
            <option value="">Select a study…</option>
            {(studiesQ.data?.rows ?? []).map((s) => (
              <option key={s.study_id} value={s.study_id}>
                {studyLabel(s)}
              </option>
            ))}
          </select>
        </label>
        {subjectsQ.data && (
          <AccessWindowBanner windows={subjectsQ.data.windows} wildcard={subjectsQ.data.wildcard} />
        )}
        <div className="ml-auto flex items-center gap-2">
          {agingQ.data && <SdvAgingBadge buckets={agingQ.data.buckets} />}
          {studyId && !outsideWindow && (
            <RbmExportButton studyId={studyId} />
          )}
        </div>
      </div>

      {outsideWindow ? (
        <div className="card border-danger-300 bg-danger-50 p-6 text-center text-sm text-danger-800">
          <AlertTriangle className="mx-auto mb-2 h-5 w-5" />
          <div className="font-semibold">
            {(subjectsQ.error as ApiError).message}
          </div>
          <p className="mt-1 text-xs">
            Contact the site administrator to request a fresh access window.
          </p>
        </div>
      ) : !studyId ? (
        <div className="card p-8 text-center text-xs text-ink-muted">
          <Radio className="mx-auto mb-2 h-5 w-5 text-brand-500" />
          Select a study to load its assigned subjects.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[320px_1fr]">
          <SubjectListPanel
            studyId={studyId}
            subjects={subjectsQ.data?.subjects ?? []}
            loading={subjectsQ.isLoading}
            selectedSubjectId={selectedSubjectId}
            onSelect={setSelectedSubjectId}
          />
          {selectedSubjectId ? (
            <SubjectSdvPanel
              subjectId={selectedSubjectId}
              canMarkSdv={isMonitor}
              studyId={studyId}
            />
          ) : (
            <div className="card flex items-center justify-center p-10 text-xs text-ink-muted">
              Pick a subject on the left to load its SDV surface.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function AccessWindowBanner({
  windows,
  wildcard,
}: {
  windows: Array<{
    reference_number: string;
    window_start_at: string | null;
    window_end_at: string | null;
  }>;
  wildcard: boolean;
}) {
  const first = windows[0];
  if (!first) return null;
  return (
    <div className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-800">
      <ShieldCheck className="mr-1 inline h-3 w-3" />
      Access window: {first.window_start_at ? fmtDateTime(first.window_start_at) : 'open'} →{' '}
      {first.window_end_at ? fmtDateTime(first.window_end_at) : 'open'}
      {wildcard && ' · assignment covers every subject on this study'}
      {windows.length > 1 && ` · +${windows.length - 1} more window${windows.length === 2 ? '' : 's'}`}
    </div>
  );
}

function SdvAgingBadge({
  buckets,
}: {
  buckets: {
    under_3d: number;
    three_to_seven: number;
    seven_to_fourteen: number;
    over_14d: number;
  };
}) {
  const total = buckets.under_3d + buckets.three_to_seven + buckets.seven_to_fourteen + buckets.over_14d;
  if (total === 0) {
    return (
      <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-medium text-emerald-700">
        <CheckCircle2 className="mr-0.5 inline h-2.5 w-2.5" />
        SDV up to date
      </span>
    );
  }
  return (
    <div className="flex items-center gap-1 text-[10px]">
      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-700">&lt;3d {buckets.under_3d}</span>
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-700">3-7d {buckets.three_to_seven}</span>
      <span className="rounded bg-orange-100 px-1.5 py-0.5 text-orange-700">7-14d {buckets.seven_to_fourteen}</span>
      <span className="rounded bg-danger-100 px-1.5 py-0.5 text-danger-700">&gt;14d {buckets.over_14d}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SubjectListPanel({
  subjects,
  loading,
  selectedSubjectId,
  onSelect,
}: {
  studyId: string;
  subjects: Array<{ subject_id: string; external_ref: string; ivrf_number?: string | null; status: string }>;
  loading: boolean;
  selectedSubjectId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <aside className="card p-3">
      <h3 className="mb-2 text-xs font-semibold text-ink-primary">
        Assigned subjects {subjects.length > 0 && <>({subjects.length})</>}
      </h3>
      {loading ? (
        <div className="text-[11px] text-ink-muted">
          <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading…
        </div>
      ) : subjects.length === 0 ? (
        <p className="text-[11px] text-ink-muted">
          No subjects assigned to you on this study.
        </p>
      ) : (
        <ul className="space-y-1">
          {subjects.map((s) => (
            <li key={s.subject_id}>
              <button
                type="button"
                onClick={() => onSelect(s.subject_id)}
                className={`w-full rounded border px-2 py-1.5 text-left text-xs hover:bg-surface-muted/40 ${
                  selectedSubjectId === s.subject_id
                    ? 'border-accent-400 bg-accent-50/40'
                    : 'border-surface-border bg-surface-card'
                }`}
              >
                <div className="font-mono text-[11px] text-ink-secondary">
                  {subjectDisplayId(s)}
                </div>
                <div className="mt-0.5 text-[10px] text-ink-muted capitalize">
                  {s.status.replace(/_/g, ' ')}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------

function SubjectSdvPanel({
  subjectId,
  canMarkSdv,
  studyId,
}: {
  subjectId: string;
  canMarkSdv: boolean;
  studyId: string;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['remote-monitoring', 'subject', subjectId],
    queryFn: () => remoteMonitoring.getSubject(subjectId),
    retry: false,
  });

  const mark = useMutation({
    mutationFn: (formId: string) =>
      remoteMonitoring.markSdv({ subject_id: subjectId, form_id: formId }),
    onSuccess: () => {
      toast.success('SDV verified.');
      qc.invalidateQueries({ queryKey: ['remote-monitoring', 'subject', subjectId] });
      qc.invalidateQueries({ queryKey: ['remote-monitoring', 'sdv-aging', studyId] });
    },
    onError: (e) => toast.error(errorText(e, 'Mark failed')),
  });

  if (q.isLoading) {
    return (
      <div className="card p-8 text-center text-xs text-ink-muted">
        <Loader2 className="mx-auto h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="card p-8 text-center text-xs text-danger-600">
        Failed to load subject SDV surface.
      </div>
    );
  }

  const { subject, instances, verifications, config } = q.data;
  const groupedByVisit = groupByVisit(instances);

  return (
    <div className="card p-3">
      <header className="mb-3 flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-ink-primary">
            {subjectDisplayId(subject)}
          </h3>
          <p className="mt-0.5 text-[11px] text-ink-muted capitalize">
            Status: {subject.status.replace(/_/g, ' ')} · SDV granularity{' '}
            <span className="font-mono">{config.granularity}</span>
            {config.default && ' (default)'}
          </p>
        </div>
        <span className="rounded border border-surface-border px-2 py-1 text-[10px] text-ink-muted">
          <ShieldCheck className="mr-0.5 inline h-2.5 w-2.5" />
          {verifications.length} verification{verifications.length === 1 ? '' : 's'}
        </span>
      </header>

      {instances.length === 0 ? (
        <p className="rounded border border-dashed border-surface-border p-4 text-center text-xs text-ink-muted">
          No form instances captured for this subject yet.
        </p>
      ) : (
        <div className="space-y-3">
          {Array.from(groupedByVisit.entries()).map(([visitId, rows]) => (
            <div
              key={visitId}
              className="rounded border border-surface-border p-2"
            >
              <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold text-ink-primary">
                <span>
                  {rows[0]?.visit_name || 'Visit'}
                  {rows[0]?.visit_status && (
                    <span className="ml-1.5 font-normal capitalize text-ink-muted">
                      · {rows[0].visit_status.replace(/_/g, ' ')}
                    </span>
                  )}
                  {/* Visit dates for verification (23 Oct 2026). */}
                  <span className="ml-1.5 font-normal text-ink-muted">
                    · Planned {rows[0]?.visit_planned_at ? fmtVisitDay(rows[0].visit_planned_at) : '—'}
                    {' · '}
                    {(() => {
                      const a = visitActualDateLabel({
                        actual_date: rows[0]?.visit_actual_date,
                        status: rows[0]?.visit_status,
                      });
                      return a.recorded ? <span className="text-ink-secondary">Actual {a.text}</span> : a.text;
                    })()}
                  </span>
                </span>
                <span className="rounded bg-surface-muted/40 px-1.5 py-0.5 text-[10px] font-normal text-ink-muted">
                  {rows.filter((r) => r.sdv_status === 'complete').length} /{' '}
                  {rows.length} SDV-verified
                </span>
              </div>
              <ul className="space-y-1">
                {rows.map((inst) => (
                  <li
                    key={inst.instance_id}
                    className="flex items-center justify-between rounded border border-surface-border bg-surface-card p-1.5 text-[11px]"
                  >
                    <div>
                      <div className="text-ink-primary">
                        {inst.form_name || 'Form'}
                        {inst.form_reference_number && (
                          <span className="ml-1.5 font-mono text-[10px] text-ink-muted">
                            {inst.form_reference_number}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1 text-[10px] text-ink-muted">
                        <Clock className="h-2.5 w-2.5" />
                        {inst.submitted_at
                          ? `Submitted ${fmtDateTime(inst.submitted_at)}`
                          : 'Not submitted yet'}
                        {inst.sdv_verified_at && (
                          <>
                            {' · '}
                            SDV {fmtDateTime(inst.sdv_verified_at)}
                            {inst.sdv_reference_number && (
                              <span className="ml-1 font-mono">
                                {inst.sdv_reference_number}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <span
                        className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${SDV_BADGE[inst.sdv_status]}`}
                      >
                        {SDV_LABEL[inst.sdv_status]}
                      </span>
                      {canMarkSdv && inst.sdv_status !== 'complete' && (
                        <button
                          type="button"
                          onClick={() => mark.mutate(inst.form_id)}
                          disabled={mark.isPending}
                          className="rounded bg-accent-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-accent-700 disabled:opacity-50"
                        >
                          Mark SDV
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Grouped per visit, in visit order (schedule order, then name). */
function groupByVisit<T extends { visit_id: string; visit_order?: number | null; visit_name?: string | null }>(
  rows: T[],
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  const sorted = [...rows].sort(
    (a, b) =>
      (a.visit_order ?? Number.MAX_SAFE_INTEGER) - (b.visit_order ?? Number.MAX_SAFE_INTEGER) ||
      (a.visit_name ?? '').localeCompare(b.visit_name ?? ''),
  );
  for (const r of sorted) {
    if (!map.has(r.visit_id)) map.set(r.visit_id, []);
    map.get(r.visit_id)!.push(r);
  }
  return map;
}

// ---------------------------------------------------------------------------

function RbmExportButton({ studyId }: { studyId: string }) {
  const run = useMutation({
    mutationFn: () =>
      remoteMonitoring.rbmExport({ study_id: studyId, format: 'json' }),
    onSuccess: (r) => {
      // Trigger a client-side download of the payload so the CRA lands
      // with a file even though the current MVP doesn't stage into S3.
      const blob = new Blob([r.payload], {
        type: r.format === 'csv' ? 'text/csv' : 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rbm-export-${new Date().toISOString().slice(0, 10)}.${r.format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('RBM export ready.');
    },
    onError: (e) => toast.error(errorText(e, 'Export failed')),
  });
  return (
    <button
      type="button"
      onClick={() => run.mutate()}
      disabled={run.isPending}
      className="rounded border border-surface-border px-2 py-1.5 text-xs font-medium hover:bg-surface-muted/50 disabled:opacity-50"
    >
      <Download className="mr-1 inline h-3 w-3" />
      {run.isPending ? 'Exporting…' : 'RBM export'}
    </button>
  );
}
